/**
 * Structured Data Utility
 * 
 * This module provides functions to generate Schema.org structured data
 * in JSON-LD format for the Dasha.AI website.
 */

// Schema.org constants
export const SCHEMA_CONTEXT = "https://schema.org";
export const BASE_URL = "https://dasha.ai";

export const SCHEMA_TYPES = {
  ORGANIZATION: 'Organization',
  BLOG_POSTING: 'BlogPosting',
  WEB_PAGE: 'WebPage'
};

/**
 * Main function to generate structured data based on type
 * @param {Object} options - Configuration object
 * @param {string} options.type - Schema type (ORGANIZATION, BLOG_POSTING, WEB_PAGE)
 * @param {Object} options.data - Data specific to the schema type
 * @param {Object} options.location - Gatsby location object
 * @param {Object} options.siteMetadata - Site metadata (title, description, image, siteUrl)
 * @returns {Object|null} JSON-LD structured data object
 */
export function generateStructuredData({ type, data = {}, location, siteMetadata }) {
  try {
    switch (type) {
      case SCHEMA_TYPES.ORGANIZATION:
        return generateOrganizationSchema();
      case SCHEMA_TYPES.BLOG_POSTING:
        return generateBlogPostingSchema(data, location);
      case SCHEMA_TYPES.WEB_PAGE:
        return generateWebPageSchema(data, location, siteMetadata);
      default:
        console.warn(`Unknown schema type: ${type}`);
        return null;
    }
  } catch (error) {
    console.error('Error generating structured data:', error);
    return null;
  }
}

/**
 * Generate Organization schema for homepage
 * @returns {Object} Organization schema
 */
function generateOrganizationSchema() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Organization",
    "name": "Dasha.AI",
    "url": BASE_URL,
    "logo": `${BASE_URL}/thumbnail.png`,
    "sameAs": [
      "https://www.linkedin.com/company/dashaai/",
      "https://twitter.com/DashaAI_/",
      "https://github.com/dasha-samples",
      "https://www.crunchbase.com/organization/dasha-ai-inc",
      "https://pitchbook.com/profiles/company/266173-75"
    ],
    "description": "Dasha.AI empowers developers to create ultra-realistic voice and text AI agents, enhancing customer interactions and automating processes seamlessly.",
    "founder": {
      "@type": "Person",
      "name": "Vlad Chernyshov"
    },
    "foundingDate": "2019",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "885 Third Avenue 24th floor",
      "addressLocality": "New York City",
      "addressRegion": "NY",
      "postalCode": "10022",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "support@dasha.ai",
      "contactType": "Customer Support",
      "areaServed": "Worldwide",
      "availableLanguage": ["English", "Russian"]
    }
  };
}

/**
 * Generate BlogPosting schema with @graph structure
 * @param {Object} data - Blog post data
 * @param {Object} data.post - Post specific data
 * @param {string} data.post.title - Post title
 * @param {string} data.post.author - Author name
 * @param {string} data.post.datePublished - Publication date (ISO format)
 * @param {string} data.post.dateModified - Modification date (ISO format)
 * @param {string} data.post.image - Hero image URL (relative path)
 * @param {string} data.post.description - Post description
 * @param {Object} location - Gatsby location object
 * @returns {Object} BlogPosting schema with @graph structure
 */
function generateBlogPostingSchema(data, location) {
  const { post } = data;
  const postUrl = `${BASE_URL}${location.pathname}`;
  
  // Handle image URL - make absolute if relative
  let imageUrl = `${BASE_URL}/thumbnail.png`; // Default fallback
  if (post.image) {
    imageUrl = post.image.startsWith('http') ? post.image : `${BASE_URL}${post.image}`;
  }
  
  return {
    "@context": SCHEMA_CONTEXT,
    "@graph": [
      {
        "@type": "ItemList",
        "itemListElement": {
          "@type": "BlogPosting",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": postUrl
          },
          "headline": post.title,
          "author": {
            "@type": "Person",
            "name": post.author || "Dasha.AI Team"
          },
          "datePublished": post.datePublished,
          "dateModified": post.dateModified || post.datePublished
        }
      },
      {
        "@type": "BlogPosting",
        "@id": postUrl,
        "headline": post.title,
        "image": [imageUrl],
        "datePublished": post.datePublished,
        "dateModified": post.dateModified || post.datePublished,
        "author": {
          "@type": "Person",
          "name": post.author || "Dasha.AI Team"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Dasha.AI",
          "url": BASE_URL,
          "logo": {
            "@type": "ImageObject",
            "url": `${BASE_URL}/thumbnail.png`
          },
          "sameAs": [
            "https://www.linkedin.com/company/dashaai/",
            "https://twitter.com/DashaAI_/",
            "https://github.com/dasha-samples",
            "https://www.crunchbase.com/organization/dasha-ai-inc",
            "https://pitchbook.com/profiles/company/266173-75"
          ]
        }
      }
    ]
  };
}

/**
 * Generate WebPage schema for general pages
 * @param {Object} data - Page data
 * @param {string} data.name - Page name/title
 * @param {string} data.description - Page description
 * @param {string} data.path - Page path (optional, will use location.pathname if not provided)
 * @param {Object} location - Gatsby location object
 * @param {Object} siteMetadata - Site metadata
 * @returns {Object} WebPage schema
 */
function generateWebPageSchema(data, location, siteMetadata) {
  const { name, description, path } = data;
  const pageUrl = `${BASE_URL}${path || location.pathname}`;
  
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "WebPage",
    "name": name || siteMetadata.title,
    "description": description || siteMetadata.description,
    "url": pageUrl,
    "isPartOf": {
      "@type": "WebSite",
      "@id": BASE_URL
    },
    "about": {
      "@type": "Organization",
      "name": "Dasha.AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Dasha.AI",
      "url": BASE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/thumbnail.png`
      }
    }
  };
}